/**
 * Feature-local contracts for Action View API data that is not currently emitted to model.d.ts/enums.d.ts.
 *
 * Angular code should use the camel-cased shapes in this file. Saved Action View criteria use a legacy
 * Pascal-cased master-filter schema; that persisted representation must be handled by a boundary adapter.
 */

import type { CostCenterTeam } from '../../Team/teamPicker.component';

export enum TaskActionViewShowOption {
    AllTasks = 1,
    MyTasks = 2,
    MyTeamsTasks = 3,
    SpecificIndividualsTasks = 4,
    SpecificTeamsTasks = 5,
}

export enum TaskActionViewIncludeOption {
    AllCompleted = 1,
    CompletedByMe = 2,
    CompletedByMyTeam = 3,
    CompletedBySpecificIndividual = 4,
    CompletedBySpecificTeam = 5,
}

export enum TaskActionViewWhereOption {
    Assigned = 1,
    UltimatelyResponsible = 2,
    AssignedOrUltimatelyResponsible = 3,
    AccountHandler = 4,
    ComplianceReturnsPPResp = 5,
}

export enum TaskActionViewThatAreOption {
    Ready = 1,
    NotReady = 2,
    ReadyOrNotReady = 3,
    Completed = 4,
}

export enum TaskActionViewCompletedOption {
    DatesProvidedByUser = 0,
    CompletedWithinLastDays = 1,
}

export enum TaskActionViewDateTimeOption {
    AllDates = 1,
    DueWithinDays = 2,
    DueDateRange = 3,
    Urgency = 4,
}

export enum TaskActionViewUrgencyOption {
    Overdue = 1,
    Critical = 2,
    Imminent = 3,
    Approaching = 4,
    Prospective = 5,
    CriticalOrImminent = 6,
}

export enum TaskActionViewOrganizeBy {
    Urgency = 1,
    DueDate = 2,
    LastActivityDate = 3,
}

export interface ActionViewRouteParams {
    actionViewId?: number;
    actionViewType?: number;
    displayType?: number;
}

export type ActionViewRouteState = 'actionview';

export interface ActionViewRouteContext {
    state: ActionViewRouteState;
    params: ActionViewRouteParams;
}

export interface ActionViewUserTeamSelection {
    teamID: number | null;
    userID: string;
    isDefault: boolean | null;
}

export interface ActionViewTeamSelection {
    teamID: number;
}

export interface ActionViewPickerItem {
    contactID?: number;
    teamID?: number | null;
    userID?: System.Guid | string;
    isDefault?: boolean | null;
    user?: Core.UserTeamModel & Core.ContactModel;
    team?: CostCenterTeam;
    name?: string;
}

export interface ActionViewAdditionalWhereFilter {
    whereChoice: TaskActionViewWhereOption;
    userID: string | null;
    userTeams: ActionViewUserTeamSelection[];
    teams: ActionViewTeamSelection[];
}

export interface ActionViewOutputColumn {
    id: number;
    sequence: number;
    displayName?: string;
    depthValue?: number;
    resultColumnName?: string;
    canGroupTaskActionView?: boolean;
    fromTaskType?: boolean;
}

export interface ActionViewUiOperator {
    name: string;
    displayName?: string;
    sortOrder?: number;
}

export interface ActionViewUiFilterElement {
    operator: ActionViewUiOperator;
    value: string;
}

export interface ActionViewUiColumn {
    id?: number;
    columnId?: number;
    advancedSearchFieldID?: number;
    sequence: number;
    displayName: string;
    resultColumnName?: string;
    resultsColumnName?: string;
    depthValue: number;
    type?: string;
    operators: ActionViewUiOperator[];
    or: ActionViewUiFilterElement[];
    canGroupTaskActionView: boolean;
    fromTaskType?: boolean;
    isRemovable?: boolean;
}

export interface ActionViewSearchRequest extends Partial<Core.SearchRequestBase> {
    taskSeriesTypeID: number;
    taskTypeID: number;
    taskTypeName?: string;
    showChoice: TaskActionViewShowOption;
    whereChoice: TaskActionViewWhereOption;
    thatAreChoice: TaskActionViewThatAreOption;
    includeChoice: TaskActionViewIncludeOption;
    showUserTeams: ActionViewPickerItem[];
    showTeams: ActionViewPickerItem[];
    includeUserTeams: ActionViewPickerItem[];
    includeTeams: ActionViewPickerItem[];
    additionalWhereFilters: ActionViewAdditionalWhereFilter[];
    dateTimeChoice: TaskActionViewDateTimeOption;
    dueWithinDays: number | null;
    useUrgencyFilter: boolean | null;
    urgencyChoice: TaskActionViewUrgencyOption | null;
    includeUnscheduled: boolean;
    onlyUnscheduled: boolean | null;
    includeSkipped: boolean;
    onlySkipped: boolean | null;
    dueDateFrom: Date | string | null;
    dueDateTo: Date | string | null;
    useLastActivityFilter: boolean | null;
    lastActivityFrom: number | null;
    lastActivityTo: number | null;
    lastActivityTimeSpan: string | null;
    completedDateFrom: Date | string | null;
    completedDateTo: Date | string | null;
    completedWithinDays: number | null;
    completedChoice: TaskActionViewCompletedOption;
    filters: Core.AdvancedSearchFilter[];
    entityRelatedFilters: Core.AdvancedSearchFilter[];
    taskOutputColumnIds: number[];
    entityOutputColumnIds: number[];
    addedTaskOutputColumnIds: number[];
    addedEntityOutputColumnIds: number[];
    taskOutputColumns: ActionViewOutputColumn[];
    entityOutputColumns: ActionViewOutputColumn[];
    customOutput: boolean;
    output: 'default' | 'custom';
    detailedResults: boolean;
    organizeBy: TaskActionViewOrganizeBy;
    groupBy: number[];
    addedGroupBy: number[];
    calculateReadyAndNotReady: boolean;
    showMostPressingTasks: boolean;
    outputType: Core.AdvancedSearchRequestOutputTypes;
    outputInt: number;
    justReturnError: boolean | null;
    searchName: string | null;
    mostPressingNotReadyOnly: boolean | null;
    persistCustomColumns?: boolean;
    showTotals: boolean;
    showOrs: boolean;
}

export interface ActionViewOverviewGroup {
    name: string;
    value: string;
    label: string;
    id: string;
}

export interface ActionViewOverviewDataItem {
    label: string;
    value: number;
    ready: number | null;
    notReady: number | null;
    sequence: number;
}

export interface ActionViewOverviewResult {
    taskSeriesTypeID: number;
    taskTypeID: number;
    taskSeriesTypeName: string;
    name: string;
    firstGroup: ActionViewOverviewGroup | null;
    secondGroup: ActionViewOverviewGroup | null;
    data: ActionViewOverviewDataItem[];
}

export interface ActionViewDetailedRow {
    [columnName: string]: unknown;
    n10908?: number;
    error?: string | boolean;
    warning?: string | boolean;
    stale?: boolean;
}

export interface ActionViewUrgency {
    id: TaskActionViewUrgencyOption;
    display: string;
}

export interface ActionViewSearchResult {
    dataTable: ActionViewDetailedRow[];
    group1ResultList: ActionViewOverviewResult[];
    isCancelled: boolean;
    totalCount: number | null;
    searchTimeMilliseconds: number;
    searchTimestamp: Date | string;
    searchName: string | null;
    savedSearchID: number | null;
    serverError: string | null;
    sql: string | null;
    sqlParamDebugInfo: string | null;
    log: unknown;
}

export interface ActionViewApiColumn {
    displayName: string;
    operators: string[];
    type: string;
    sequence: number;
    columnId: number;
    depthValue: number;
    resultColumnName: string;
    usedBySmart: boolean;
    usedByTaskActionView: boolean;
    canGroupTaskActionView: boolean;
    canBeUsedAsFilterCriteria: boolean;
    isRyanInternal: boolean;
    categoryId: number;
}

export interface ActionViewTaskType {
    taskTypeID: number;
    taskSeriesTypeID: number;
    sequence: number;
    name: string;
    completedName: string;
    currentName: string;
    anticipatedAttachmentTypeID: number | null;
    deliverableName: string | null;
    systemCompletes: boolean | null;
    authorizationRequired: boolean;
    systemRescinds: boolean;
    systemDeletes: boolean;
    systemReassigns: boolean;
    systemSkips: boolean;
    csrForPerm: number | null;
    ahForPerm: boolean;
    jsForPerm: boolean;
    useExistingForPerm: boolean;
    specialPerm: boolean;
    permissionNotes: string | null;
    taskSeriesTypeSequence: number;
    taskSeriesTypeAdvancedSearchDepthID: number | null;
    taskSeriesTypeAdvancedSearchDepthValue: number | null;
    columnIds: number[];
    depthValuesToEnable: number[];
    fieldIdsToEnable: number[];
}

export interface ActionViewTaskTypeOptions {
    taskTypes: ActionViewTaskType[];
    columns: ActionViewApiColumn[];
}

export interface ActionViewWelcomeSearchResult extends ActionViewSearchResult {
    originalRequest: ActionViewSearchRequest;
    columnReferenceLookup: Record<number, ActionViewApiColumn>;
}

export interface ActionViewContextOption {
    optionId: number;
    text: string;
}

export interface ActionViewContextOptionResult {
    singleInstanceId: number | null;
    options: ActionViewContextOption[];
}

export interface ActionViewSavedCriteria {
    outputColumns: number[];
    filters: Core.AdvancedSearchFilter[];
    masterFilter: ActionViewSearchRequest;
}

export interface ActionViewSavedSearch {
    actionViewName: string;
    actionViewId: number;
    criteria: string | Record<string, unknown>;
    categoryId: number | null;
    categoryName: string | null;
    isRyanInternal: boolean | null;
    isFavorite: boolean | null;
    defaultDisplay: number;
    sequence: number;
    isShared: boolean;
    isOwnedByUser: boolean;
    userID: string | null;
    rowVersion: number[];
    isSystemSearch: boolean | null;
}

export interface ActionViewExternalDrilldown {
    task: ActionViewOverviewResult;
    item: ActionViewOverviewDataItem;
    ready: boolean | null;
}

export interface ActionViewOverviewTotals {
    value: number;
    ready: number;
    notReady: number;
}

export interface ActionViewOverviewSnapshot {
    results: ActionViewOverviewResult[];
    headers: string[];
    organizeTotals: ActionViewOverviewDataItem[];
    grandTotals: ActionViewOverviewTotals;
    groupByCount: 0 | 1 | 2;
}

export interface ActionViewGridSnapshot {
    columnState: unknown[];
    filterState: Record<string, unknown> | null;
    selectedTaskIds: number[];
}

export interface ActionViewDetailSnapshot {
    results: ActionViewSearchResult;
    grid: ActionViewGridSnapshot | null;
}

export interface ActionViewDefinition {
    actionViewId: number | null;
    actionViewName: string;
    actionViewType: number | null;
    categoryId: number | null;
    defaultDisplay: number;
    isFavorite: boolean;
    isShared: boolean;
    isOwnedByUser: boolean;
    rowVersion: number[];
}

export type ActionViewDisplay = 'filter' | 'overview' | 'detailed';

export interface ActionViewSessionState {
    route: ActionViewRouteContext;
    definition: ActionViewDefinition;
    display: ActionViewDisplay;
    masterCriteria: ActionViewSearchRequest;
    filterColumns: ActionViewUiColumn[];
    drilldownCriteria: ActionViewSearchRequest | null;
    overview: ActionViewOverviewSnapshot | null;
    isOverviewTimedOut: boolean;
    detail: ActionViewDetailSnapshot | null;
    externalDrilldown: ActionViewExternalDrilldown | null;
    showOrs: boolean;
    drilldownMode: boolean;
}
